import styled from "@emotion/styled";
import type { FC } from "react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { ErrorCode } from "src/constants/errorCodes";
import type { TranslationKey } from "src/types/translationKey";
import BlockingLoadingOverlayController from "src/ui/components/BlockingLoadingOverlay/BlockingLoadingOverlayController";
import OtpInput, { OtpInputRef } from "src/ui/components/OtpInput/OtpInput";
import Translate from "src/ui/components/Translate/Translate";
import { userEmailVerificationState, userPreferences } from "src/state/state";
import { Button } from "@9amhealth/shared";
import { APP_BREAKPOINT } from "src/constants/layout";

export interface EmailVerificationStepProps {
  email: string;
  verificationToken: string;
  onSuccess: () => void;
}

const Container = styled.div`
  text-align: center;
  width: 100%;
  margin: 0 auto;

  @media (pointer: coarse) {
    &:has(input:focus) {
      padding-bottom: calc(var(--stored-keyboard-height, 35vh) + 3rem);
    }
  }
`;

const Title = styled.h4`
  color: var(--Greys-Charcoal, #212121);
  text-align: center;
  font-feature-settings:
    "liga" off,
    "clig" off;

  font-family: var(--font-family-serif, "STK Bureau Serif"), Serif;
  font-size: 28px;
  font-style: normal;
  font-weight: 300;
  line-height: 110%;

  margin: 0 0 0.5rem;
  letter-spacing: -0.56px;

  @media (min-width: ${APP_BREAKPOINT}px) {
    font-size: 40px;
    letter-spacing: -1px;
  }
`;

const ResendContainer = styled.div`
  margin-top: 5rem;
`;

const ResendText = styled.p`
  color: var(--color-c-60, #888);
  margin: 0;
  font-size: 0.875rem;
`;

const ErrorMessage = styled.div`
  text-align: center;
  color: var(--color-error, #bf1902);
  margin-top: 0.5rem;
`;

const formatTime = (seconds: number): string => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
};

const getErrorMessage = (errorCode: string): TranslationKey => {
  switch (errorCode) {
    case ErrorCode.emailVerificationFailed:
      return "emailVerification.invalidCode";
    case ErrorCode.emailVerificationCodeExpired:
      return "emailVerification.expiredCode";
    default:
      return "emailVerification.invalidCode";
  }
};

interface ResendTimerProps {
  onResend: () => Promise<void>;
  isResending: boolean;
  resetKey: number;
}

const ResendTimer: FC<ResendTimerProps> = ({
  onResend,
  isResending,
  resetKey
}) => {
  const [secondsRemaining, setSecondsRemaining] = useState(() =>
    userEmailVerificationState.getRemainingSeconds()
  );
  const [canResend, setCanResend] = useState(() =>
    userEmailVerificationState.canResend()
  );
  const lastResetKey = useRef(resetKey);

  useEffect(() => {
    if (resetKey !== lastResetKey.current) {
      lastResetKey.current = resetKey;
      setSecondsRemaining(userEmailVerificationState.resendCooldownSeconds);
      setCanResend(false);
    }
  }, [resetKey]);

  useEffect(() => {
    if (secondsRemaining <= 0) {
      setCanResend(true);
      return undefined;
    }

    const timer = setTimeout(() => {
      setSecondsRemaining((s) => s - 1);
    }, 1000);

    return () => {
      clearTimeout(timer);
    };
  }, [secondsRemaining]);

  const handleClick = () => {
    if (canResend && !isResending) {
      void onResend();
    }
  };

  return (
    <ResendContainer>
      {canResend ? (
        <Button
          onClick={handleClick}
          disabled={isResending}
          theme="transparent"
          center
        >
          <Translate msg="emailVerification.resend" />
        </Button>
      ) : (
        <ResendText>
          <Translate
            msg="emailVerification.resend_countdown"
            variables={{ time: formatTime(secondsRemaining) }}
          />
        </ResendText>
      )}
    </ResendContainer>
  );
};

const EmailVerificationStep: FC<EmailVerificationStepProps> = ({
  email,
  verificationToken,
  onSuccess
}) => {
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [error, setError] = useState<TranslationKey | null>(null);
  const [currentToken, setCurrentToken] = useState(verificationToken);
  const [timerResetKey, setTimerResetKey] = useState(0);
  const otpInputRef = useRef<OtpInputRef>(null);

  const handleVerify = useCallback(
    async (code: string) => {
      if (isVerifying) return;

      setIsVerifying(true);
      setError(null);
      BlockingLoadingOverlayController.startLoading({
        bg: "transparent"
      });

      const result = await userEmailVerificationState.verifyByCode({
        email,
        code,
        verificationToken: currentToken
      });

      if (result.success) {
        BlockingLoadingOverlayController.loadingSuccess();
        onSuccess();
      } else {
        BlockingLoadingOverlayController.endLoading();
        setError(getErrorMessage(result.errorCode ?? ""));
        otpInputRef.current?.clear();
      }

      setIsVerifying(false);
    },
    [email, currentToken, onSuccess, isVerifying]
  );

  const handleResend = useCallback(async () => {
    if (isResending) return;

    setIsResending(true);
    setError(null);

    try {
      const newToken =
        await userEmailVerificationState.resendVerification(email);

      if (newToken) {
        setCurrentToken(newToken);
      }

      setTimerResetKey((k) => k + 1);
      otpInputRef.current?.clear();
      otpInputRef.current?.focus();
    } catch {
      setError("emailVerification.invalidCode");
    } finally {
      setIsResending(false);
    }
  }, [email, isResending]);

  return (
    <Container>
      <Title>
        <Translate
          msg="emailVerification.title"
          variables={{ firstName: userPreferences.firstName }}
        />
      </Title>
      <p
        className="as-body"
        style={{ color: "var(--Greys-Charcoal80, rgba(33, 33, 33, 0.8))" }}
      >
        <Translate msg="emailVerification.subtitle" variables={{ email }} />
      </p>

      <div style={{ marginTop: "2.5rem" }}>
        <OtpInput
          ref={otpInputRef}
          onComplete={(code) => void handleVerify(code)}
          disabled={isVerifying}
          error={Boolean(error)}
        />
      </div>

      {error && (
        <ErrorMessage>
          <Translate msg={error} />
        </ErrorMessage>
      )}

      <ResendTimer
        onResend={handleResend}
        isResending={isResending}
        resetKey={timerResetKey}
      />
    </Container>
  );
};

export default EmailVerificationStep;
